// Phase 37 - capture paid-traffic click IDs + campaign tags on ARRIVAL and
// carry them through to every lead submission.
//
// The problem this solves: Google Ads appends `gclid=` to the landing URL.
// Every form used to send only the URL at submission time (`source_url`),
// so a visitor who lands on `/solar-installers/gold-coast?gclid=…` and then
// clicks through to `/contact` submitted with a clean URL - the click ID was
// gone and the lead could no longer be matched to the Google Ads click. The
// same loss hits organic: `document.referrer` is only reliable on the
// first page (and is lost entirely after a reload / new tab).
//
// How it works:
//   - `captureAttribution()` runs once at app boot (App.js). If the URL
//     carries any click ID or utm_* tag it stores a fresh attribution
//     record (last campaign touch wins). A plain organic / direct landing
//     only stores a record when there is no paid record already held, so a
//     visitor who clicked an ad on Monday and comes back via Google organic
//     on Friday is still reported as the Google Ads click - matching how
//     Google Ads itself attributes the conversion (90-day click window).
//   - `getAttribution()` returns the stored fields, spread into every lead
//     POST body next to `source_url` / `referrer`. When no gclid was stored
//     it falls back to the `_gcl_aw` cookie Google's tag writes on a gclid
//     landing, so returning visitors are still matched.
//
// Storage is localStorage (90-day TTL, mirrors Google's click window). Every
// access is wrapped in try/catch - private mode / blocked storage must never
// break a form.

export const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "dclid", "fbclid", "msclkid", "ttclid"];
export const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
export const ATTRIBUTION_FIELDS = [...CLICK_ID_PARAMS, ...UTM_PARAMS, "landing_page", "landing_referrer", "landed_at"];

export const STORAGE_KEY = "qv_attribution_v1";
export const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_LEN = 512;

const clip = (v) => (typeof v === "string" ? v.trim().slice(0, MAX_LEN) : "");

const hostOf = (url) => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch (_) {
    return "";
  }
};

export function readStored(now = Date.now()) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec || typeof rec !== "object") return null;
    if (!Number.isFinite(rec.captured_at) || now - rec.captured_at > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return rec;
  } catch (_) {
    return null;
  }
}

function writeStored(rec) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rec));
  } catch (_) {
    /* storage blocked - the in-memory copy below still serves this page view */
  }
}

// In-memory copy so a blocked localStorage still carries attribution across
// SPA navigations within the same page load.
let memoryRecord = null;

/** Pick the click-ID / utm params present in a querystring. */
export function paramsFromSearch(search) {
  const out = {};
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch (_) {
    return out;
  }
  for (const key of [...CLICK_ID_PARAMS, ...UTM_PARAMS]) {
    const v = clip(params.get(key) || "");
    if (v) out[key] = v;
  }
  return out;
}

const hasClickId = (rec) => !!rec && CLICK_ID_PARAMS.some((k) => rec[k]);
const hasCampaign = (rec) => !!rec && (hasClickId(rec) || UTM_PARAMS.some((k) => rec[k]));

/**
 * Inspect the current location + referrer and update the stored record.
 * Idempotent per page load: internal SPA navigations never carry click IDs,
 * and an internal referrer never displaces a stored record.
 *
 * @returns the record now in effect (or null when nothing is known).
 */
export function captureAttribution({ location, referrer, now } = {}) {
  if (typeof window === "undefined") return null;
  const loc = location || window.location;
  const ref = typeof referrer === "string" ? referrer : (typeof document !== "undefined" ? document.referrer : "") || "";
  const ts = Number.isFinite(now) ? now : Date.now();

  const incoming = paramsFromSearch(loc.search);
  const stored = readStored(ts);
  const refHost = hostOf(ref);
  const external = !!refHost && refHost !== (loc.hostname || "").toLowerCase();

  const fresh = () => ({
    ...incoming,
    landing_page: `${loc.protocol}//${loc.host}${loc.pathname || "/"}${loc.search || ""}`,
    landing_referrer: clip(ref) || null,
    landed_at: new Date(ts).toISOString(),
    captured_at: ts,
  });

  let next = stored;
  if (hasCampaign(incoming)) {
    // A new ad click / tagged campaign link always wins.
    next = fresh();
  } else if (!stored) {
    // First thing we know about this visitor: record where they came from
    // even for organic / direct so the referrer survives navigation.
    next = fresh();
  } else if (external && !hasCampaign(stored)) {
    // Organic / referral landing with only an older organic record - the
    // latest external entry is the better answer.
    next = fresh();
  }
  if (next !== stored) writeStored(next);
  memoryRecord = next;
  return next;
}

/** Parse Google's `_gcl_aw` / `_gcl_gb` cookies (`GCL.<ts>.<id>`). */
export function googleClickCookie(name, cookieString) {
  const jar = typeof cookieString === "string" ? cookieString : (typeof document !== "undefined" ? document.cookie : "") || "";
  for (const part of jar.split(";")) {
    const [k, ...rest] = part.trim().split("=");
    if (k !== name) continue;
    const v = decodeURIComponent(rest.join("="));
    const m = /^GCL\.\d+\.(.+)$/.exec(v);
    if (m && m[1]) return clip(m[1]);
  }
  return null;
}

/**
 * Attribution fields to spread into a lead POST body. Every key is always
 * present (null when unknown) so the backend payload shape is stable.
 */
export function getAttribution() {
  const rec = (typeof window !== "undefined" && readStored()) || memoryRecord || {};
  const out = {};
  for (const key of ATTRIBUTION_FIELDS) out[key] = rec[key] || null;
  if (!out.gclid && !out.gbraid && !out.wbraid) {
    // Google's own tag keeps the click ID for 90 days; use it when our
    // record missed the landing (e.g. storage was cleared, or the visitor
    // first arrived before this code shipped).
    const cookieGclid = googleClickCookie("_gcl_aw");
    if (cookieGclid) out.gclid = cookieGclid;
    else {
      const cookieGbraid = googleClickCookie("_gcl_gb");
      if (cookieGbraid) out.gbraid = cookieGbraid;
    }
  }
  return out;
}

/** Boot hook - safe to call repeatedly (React strict-mode double effects). */
export function initAttribution() {
  if (typeof window === "undefined") return;
  if (window.__qvoltAttributionInit) return;
  window.__qvoltAttributionInit = true;
  try {
    captureAttribution();
  } catch (_) {
    /* never let attribution break the app */
  }
}
